<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form class="mb-3" validate-on="input lazy" @submit.prevent="updateAsset">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Update Asset (#{{ props.asset.id }})</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat"
                           @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-row>
                        <v-col class="pb-5 d-flex justify-center" cols="12">
                            <v-avatar :image="image" rounded="sm" size="100"/>
                            <v-btn flat icon size="small" @click="$refs.imageHandle.click()">
                                <v-icon icon="mdi-pencil"></v-icon>
                            </v-btn>
                            <v-file-input ref="imageHandle" v-model="formData['image']" accept="image/*" class="d-none"
                                          @change="validateFileSize"
                            />
                        </v-col>
                    </v-row>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>

                    <v-row class="border-b mb-0">
                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['asset_type_id']"
                                      :items="assetTypes"
                                      :rules="[vRules.required]"
                                      density="compact" item-title="name" item-value="id" label="Asset Type">
                            </v-select>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['label']" :rules="[vRules.required,vRules.maxLength(64),vRules.regex(/^[a-zA-Z0-9 \-]+$/, 'Only letters, numbers, spaces and dashes allowed')]"
                                          autofocus density="compact" label="Asset Name"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['customer_reference']" density="compact"
                                          label="Customer Reference (optional)" maxlength="100" clearable
                                          hint="Your internal ID or reference for this asset"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-switch v-model="showOnMap" color="primary" density="compact" hide-details
                                      label="Show on map"
                                      hint="Turn off to keep this asset off the live map. It keeps tracking and stays in the list."
                                      persistent-hint/>
                        </v-col>
                    </v-row>

                    <v-row v-if="['vehicle','equipment'].includes(selectedAssetType?.category)" class="border-b mb-0">
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['vin']" density="compact" label="VIN Number"
                                          maxlength="17" counter clearable
                                          hint="17-character Vehicle Identification Number"
                                          @update:modelValue="onVinInput"/>
                            <div v-if="vinLoading" class="d-flex align-center mt-n1 mb-2">
                                <v-progress-circular size="14" width="2" indeterminate color="primary" class="mr-2"/>
                                <span class="text-caption text-medium-emphasis">Decoding VIN...</span>
                            </div>
                            <v-card v-else-if="vinDetails && vinHasData" variant="tonal" color="success" density="compact" class="mt-n1 mb-2">
                                <v-card-text class="pa-3 d-flex align-start">
                                    <v-icon icon="mdi-car-info" size="20" class="mr-2 mt-1" color="success"/>
                                    <div class="flex-grow-1">
                                        <div class="text-caption font-weight-bold text-success mb-1">Vehicle Identified</div>
                                        <div v-if="vinDetails.manufacturer && vinDetails.manufacturer !== 'Unknown'" class="text-caption"><strong>Make:</strong> {{ vinDetails.manufacturer }}</div>
                                        <div v-if="vinDetails.vehicle_type && vinDetails.vehicle_type !== 'Unknown'" class="text-caption"><strong>Body:</strong> {{ vinDetails.vehicle_type }}</div>
                                        <div v-if="vinDetails.model_year && vinDetails.model_year !== 'Unknown'" class="text-caption"><strong>Year:</strong> {{ vinDetails.model_year }}</div>
                                        <div v-if="vinDetails.country && vinDetails.country !== 'Unknown'" class="text-caption"><strong>Country:</strong> {{ vinDetails.country }}</div>
                                    </div>
                                    <v-tooltip location="top" :text="vinDetails.check_digit_valid ? 'Valid VIN' : 'Invalid check digit'">
                                        <template #activator="{ props: tp }">
                                            <v-icon v-bind="tp" :icon="vinDetails.check_digit_valid ? 'mdi-check-circle' : 'mdi-alert-circle'" size="18" :color="vinDetails.check_digit_valid ? 'success' : 'warning'" class="ml-2 mt-1"/>
                                        </template>
                                    </v-tooltip>
                                </v-card-text>
                            </v-card>
                            <div v-else-if="vinDetails && !vinHasData" class="text-caption text-medium-emphasis mt-n1 mb-2">
                                <v-icon icon="mdi-information-outline" size="14" class="mr-1"/>VIN not recognized in our database
                            </div>
                        </v-col>
                        <v-col v-if="['admin','super_admin'].includes(user.type)" class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['acquired_vin']" density="compact"
                                          label="Acquired VIN (OBD)" readonly persistent-hint
                                          hint="Auto-detected from the OBD device (read-only)"/>
                            <div v-if="acquiredVinLoading" class="d-flex align-center mt-n1 mb-2">
                                <v-progress-circular size="14" width="2" indeterminate color="primary" class="mr-2"/>
                                <span class="text-caption text-medium-emphasis">Decoding VIN...</span>
                            </div>
                            <v-card v-else-if="acquiredVinDetails && acquiredVinHasData" variant="tonal" color="success" density="compact" class="mt-n1 mb-2">
                                <v-card-text class="pa-3 d-flex align-start">
                                    <v-icon icon="mdi-car-info" size="20" class="mr-2 mt-1" color="success"/>
                                    <div class="flex-grow-1">
                                        <div class="text-caption font-weight-bold text-success mb-1">Vehicle Identified</div>
                                        <div v-if="acquiredVinDetails.manufacturer && acquiredVinDetails.manufacturer !== 'Unknown'" class="text-caption"><strong>Make:</strong> {{ acquiredVinDetails.manufacturer }}</div>
                                        <div v-if="acquiredVinDetails.vehicle_type && acquiredVinDetails.vehicle_type !== 'Unknown'" class="text-caption"><strong>Body:</strong> {{ acquiredVinDetails.vehicle_type }}</div>
                                        <div v-if="acquiredVinDetails.model_year && acquiredVinDetails.model_year !== 'Unknown'" class="text-caption"><strong>Year:</strong> {{ acquiredVinDetails.model_year }}</div>
                                        <div v-if="acquiredVinDetails.country && acquiredVinDetails.country !== 'Unknown'" class="text-caption"><strong>Country:</strong> {{ acquiredVinDetails.country }}</div>
                                    </div>
                                    <v-tooltip location="top" :text="acquiredVinDetails.check_digit_valid ? 'Valid VIN' : 'Invalid check digit'">
                                        <template #activator="{ props: tp }">
                                            <v-icon v-bind="tp" :icon="acquiredVinDetails.check_digit_valid ? 'mdi-check-circle' : 'mdi-alert-circle'" size="18" :color="acquiredVinDetails.check_digit_valid ? 'success' : 'warning'" class="ml-2 mt-1"/>
                                        </template>
                                    </v-tooltip>
                                </v-card-text>
                            </v-card>
                            <div v-else-if="acquiredVinDetails && !acquiredVinHasData" class="text-caption text-medium-emphasis mt-n1 mb-2">
                                <v-icon icon="mdi-information-outline" size="14" class="mr-1"/>VIN not recognized in our database
                            </div>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['odometer']"
                                          density="compact" label="Initial Odometer"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['license_plate']" density="compact"
                                          label="License Plate" maxlength="15" clearable/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['owner_email']" density="compact"
                                          label="Owner Email (optional)" type="email" clearable
                                          hint="Used for CARB compliance notifications"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['driver_id']"
                                      :items="driversList"
                                      :item-title="d => `${d.user_first_name || d.first_name || ''} ${d.user_last_name || d.last_name || ''}`.trim()"
                                      clearable density="compact"
                                      item-value="id" :label="`Assign ${personLabel}`"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['vehicle_status_id']"
                                      :items="vehicleStatuses"
                                      density="compact" item-title="name" item-value="id" label="Status"/>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col v-if="user.type === 'admin'" class="py-0" cols="12" sm="6">
                            <company-select v-model="formData['company_id']"
                                      :items="companies"
                                      :rules="[vRules.required]"
                                      density="compact" label="Company"
                                      @update:modelValue="()=>{fetchDevices(); fetchAssetGroups(); fetchDriversList();}">
                            </company-select>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['asset_group_ids']"
                                      :items="assetGroups"
                                      density="compact" item-title="name" item-value="id" label="Asset Group" multiple>
                            </v-select>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['status']"
                                      :items="['enabled','disabled']"
                                      density="compact" label="Asset Status"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['device_id']" :items="devices"
                                      clearable density="compact" item-title="slno" item-value="id" label="Device">
                            </v-select>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button" @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted, onBeforeUnmount, computed} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useLoadingStore} from "@/stores/loadingStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {getCategoryConfig} from "@/composables/categoryConfig";
import placeholder from "@/assets/images/placeholder.png";
import CompanySelect from "@/components/globals/company-select.vue";

const {user} = useAuthStore();
const devices = ref([]);
const image = ref(placeholder);
const companies = ref([]);
const assetTypes = ref([]);
const assetGroups = ref([]);
const driversList = ref([]);
const vehicleStatuses = ref([]);
const formReady = ref(false);
const loadingStore = useLoadingStore();
const {showSnackbar} = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    asset: {
        type: Object
    }
});

const formData = reactive({
    valid: false,
    image: null,
    _method: 'put',
    vin: props.asset.vin ?? null,
    acquired_vin: props.asset.acquired_vin ?? null,
    license_plate: props.asset.license_plate ?? null,
    owner_email: props.asset.owner_email ?? null,
    label: props.asset.label ?? null,
    customer_reference: props.asset.customer_reference ?? null,
    status: props.asset.status?.toLowerCase() ?? 'enabled',
    odometer: props.asset.odometer ?? null,
    driver_id: props.asset.driver_id ?? null,
    company_id: props.asset.company_id ?? null,
    device_id: props.asset.device_id ?? null,
    asset_type_id: props.asset.asset_type_id ?? null,
    vehicle_status_id: props.asset.vehicle_status_id ?? null,
    asset_group_ids: props.asset.asset_groups?.map(group => group.id) ?? null,
    hidden_on_map: props.asset.hidden_on_map === true ? 1 : 0,
});

const showOnMap = computed({
    get: () => !formData['hidden_on_map'],
    set: (value) => { formData['hidden_on_map'] = value ? 0 : 1; },
});

const emits = defineEmits(['update:modelValue', 'updateAsset']);
const dialogSwitch = ref(props.modelValue);
const errors = ref([]);
const vinDetails = ref(null);
const vinLoading = ref(false);
let vinDebounceTimer = null;
const acquiredVinDetails = ref(null);
const acquiredVinLoading = ref(false);
let acquiredVinDebounceTimer = null;

onMounted(() => {
    image.value = props.asset?.photo ?? placeholder;
    // Load asset types first (controls form layout), then show form immediately
    fetchAssetTypes().finally(() => formReady.value = true);
    // Load remaining dropdown data in parallel (non-blocking)
    fetchDevices();
    fetchDriversList();
    fetchAssetGroups(false);
    fetchVehicleStatuses();
    if (user.type === 'admin') {
        fetchCompanies();
    }
    // Auto-lookup existing VINs on load
    // Prefer acquired_vin (OBD-detected) for the main preview, fall back to assigned vin
    const bestVin = (formData['acquired_vin'] && formData['acquired_vin'].trim().length === 17)
        ? formData['acquired_vin'].trim()
        : (formData['vin'] && formData['vin'].trim().length === 17 ? formData['vin'].trim() : null);
    if (bestVin) {
        onVinInput(bestVin);
    }
    // Also lookup acquired VIN separately for admins who can see both fields
    if (['admin','super_admin'].includes(user.type) && formData['acquired_vin'] && formData['acquired_vin'].trim().length === 17) {
        onAcquiredVinInput(formData['acquired_vin']);
    }
})
const selectedAssetType = computed(() => {
    if (formData['asset_type_id']) {
        return assetTypes.value.find((assetType) => assetType.id === formData['asset_type_id']);
    }
    return null;
})
const personLabel = computed(() => getCategoryConfig(selectedAssetType.value?.category).personLabel || 'Driver');
const vinHasData = computed(() => {
    if (!vinDetails.value) return false;
    const d = vinDetails.value;
    return (d.manufacturer && d.manufacturer !== 'Unknown') ||
           (d.vehicle_type && d.vehicle_type !== 'Unknown') ||
           (d.model_year && d.model_year !== 'Unknown') ||
           (d.country && d.country !== 'Unknown');
});
const acquiredVinHasData = computed(() => {
    if (!acquiredVinDetails.value) return false;
    const d = acquiredVinDetails.value;
    return (d.manufacturer && d.manufacturer !== 'Unknown') ||
           (d.vehicle_type && d.vehicle_type !== 'Unknown') ||
           (d.model_year && d.model_year !== 'Unknown') ||
           (d.country && d.country !== 'Unknown');
});
const imagePreview = () => {
    image.value = URL.createObjectURL(formData['image']);
}
const validateFileSize = () => {
    const maxSize = 1024 * 1024; // 1MB in bytes
    if (formData['image'].size > maxSize) {
        showSnackbar("File size exceeds 1MB. Please upload a smaller file.", 'error');
        return false;
    }
    imagePreview();
}

const fetchCompanies = () => {
    return send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Companies could not be loaded', 'error')
        })
}
const fetchDevices = () => {
    return send('get', `devices-list`, {asset: true, device: props.asset.device_id, company: formData['company_id']})
        .then((response) => {
            devices.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Devices could not be loaded', 'error')
        })
}
const fetchAssetTypes = () => {
    return send('get', `asset-types`, {list: true})
        .then((response) => {
            assetTypes.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Asset Types could not be loaded', 'error')
        })
}
const fetchVehicleStatuses = () => {
    return send('get', `fleet/statuses`, {list: 1})
        .then((response) => {
            vehicleStatuses.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Statuses could not be loaded', 'error')
        })
}
const fetchAssetGroups = (reset = true) => {
    reset ? formData['asset_group_ids'] = null : '';
    return send('get', `asset-groups?list=true&company=${formData['company_id']}`)
        .then((response) => {
            assetGroups.value = response.data.data;
            // Include default groups from current asset so their names display correctly
            if (props.asset.asset_groups) {
                for (const group of props.asset.asset_groups) {
                    if (!assetGroups.value.find(g => g.id === group.id)) {
                        assetGroups.value.push({ id: group.id, name: group.name });
                    }
                }
            }
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Asset groups could not be loaded', 'error')
        })
}
const fetchDriversList = () => {
    return send('get', `companies/drivers?list=true`, {company: formData['company_id']})
        .then((response) => {
            driversList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Drivers List could not be loaded', 'error')
        })
}
let currentVinLookup = null;
const onVinInput = (val) => {
    vinDetails.value = null;
    clearTimeout(vinDebounceTimer);
    const vin = (val || '').trim();
    if (vin.length !== 17) { vinLoading.value = false; return; }
    vinLoading.value = true;
    currentVinLookup = vin;
    vinDebounceTimer = setTimeout(() => {
        send('get', `vin/lookup`, {vin})
            .then((response) => {
                if (currentVinLookup !== vin) return;
                vinDetails.value = response.data?.data || null;
            })
            .catch(() => {})
            .finally(() => {
                if (currentVinLookup === vin) vinLoading.value = false;
            });
    }, 500);
};
let currentAcquiredVinLookup = null;
const onAcquiredVinInput = (val) => {
    acquiredVinDetails.value = null;
    clearTimeout(acquiredVinDebounceTimer);
    const vin = (val || '').trim();
    if (vin.length !== 17) { acquiredVinLoading.value = false; return; }
    acquiredVinLoading.value = true;
    currentAcquiredVinLookup = vin;
    acquiredVinDebounceTimer = setTimeout(() => {
        send('get', `vin/lookup`, {vin})
            .then((response) => {
                if (currentAcquiredVinLookup !== vin) return;
                acquiredVinDetails.value = response.data?.data || null;
            })
            .catch(() => {})
            .finally(() => {
                if (currentAcquiredVinLookup === vin) acquiredVinLoading.value = false;
            });
    }, 500);
};
const updateAsset = () => {
    errors.value = [];
    loadingStore.showLoading('Updating...', 'Please wait while we save your changes');
    // acquired_vin is detected by the OBD device and is read-only here; never
    // send it back so this form can't overwrite a newer OBD-detected value.
    const payload = {...formData};
    delete payload.acquired_vin;
    send('post', `assets/${props.asset.id}`, payload, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
        .then((response) => {
            showSnackbar(response.data.message);
            if (response.data.success) {
                emits('updateAsset', response.data.data.asset);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
        .finally(() => loadingStore.hideLoading());
}

onBeforeUnmount(() => {
    clearTimeout(vinDebounceTimer);
    clearTimeout(acquiredVinDebounceTimer);
});

</script>
